import { Controller } from 'react-hook-form'
import { PlanInterval } from '@dtx-company/true-common/src/types/PlanInterval'
import { PlanPurchasePlanIntervalPickerProps } from './PlanPurchasePlanIntervalPickerProps'
import { Select } from '@dtx-company/design-system/src'
import { deserializePlanIntervalQueryParam } from '@dtx-company/true-common/src/query-string-serialization/planInterval/deserializePlanIntervalQueryParam'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { useInternalPlanPurchaseFormContext } from '@app/plans/src/PlanPurchaseForm/context/useInternalPlanPurchaseFormContext'
import { useInternalPlanPurchaseProviderContext } from '../PlanPurchaseProvider/context/useInternalPlanPurchaseProviderContext'
import { useInternalPlanPurchaseProviderContextUpdate } from '../PlanPurchaseProvider/context/useInternalPlanPurchaseProviderContextUpdate'
import React, { useEffect } from 'react'

/**
 * Provides a plan interval picker for plan purchases that is hooked into the
 * PlanPurchaseForm for automatic 2-way binding and has built-in heap events.
 *
 * Option labels can be configured. Further customizations could be added as
 * needed in the future.
 *
 * Currently renders as a dropdown, could be expanded with options to render
 * other types of controls.
 */
export function PlanPurchasePlanIntervalPicker(
  props: PlanPurchasePlanIntervalPickerProps
): JSX.Element {
  const { heapId, label, optionLabels } = props
  const { planInterval, planType } = useInternalPlanPurchaseProviderContext()
  const updateContext = useInternalPlanPurchaseProviderContextUpdate()
  const { form } = useInternalPlanPurchaseFormContext()
  const value = form.watch('planInterval')

  // Sync form with context
  useEffect(() => {
    form.setValue('planInterval', planInterval)
  }, [planInterval, form])

  // Sync context with form
  useEffect(() => {
    updateContext({
      planInterval: deserializePlanIntervalQueryParam(value) || PlanInterval.YEARLY
    })
  }, [value, updateContext])

  return (
    <Controller
      name="planInterval"
      control={form.control}
      render={({ field, formState }) => {
        const { ref, ...rest } = field
        const error = formState.errors.planInterval
        return (
          <Select
            {...props}
            {...rest}
            inputRef={ref}
            label={label}
            value={value}
            options={[
              { label: optionLabels.yearly, value: PlanInterval.YEARLY },
              { label: optionLabels.monthly, value: PlanInterval.MONTHLY }
            ]}
            helperText={error?.message}
            error={Boolean(error?.message)}
            onChange={event => {
              field.onChange(event)
              updateContext({
                planInterval:
                  deserializePlanIntervalQueryParam(event.target.value) || PlanInterval.YEARLY
              })
              fireAnalyticsEvent(`${heapId}_Changed`, {
                value: event.target.value,
                planType
              })
              if (props.onChange) props.onChange(event)
            }}
            onOpen={event => {
              fireAnalyticsEvent(`${heapId}_Toggled`, {
                open: true
              })
              if (props.onOpen) props.onOpen(event)
            }}
            onClose={event => {
              fireAnalyticsEvent(`${heapId}_Toggled`, {
                open: false
              })
              if (props.onClose) props.onClose(event)
            }}
          />
        )
      }}
    />
  )
}
